import { couponCodeKey, normalizeCouponCode } from "@/lib/pending-coupon"
import type { Cart } from "@/types"
import { describeRejection, isInvalidReason, type CouponBlocker } from "./coupon-copy"
import type { CouponEvaluation } from "./types"

/** Ceiling on codes typed into one checkout; the merchant's own limit is enforced by the server. */
export const MAX_ENTERED_COUPON_CODES = 10

export type AddCouponCodeResult =
  | { ok: true; code: string; codes: string[] }
  | { ok: false; error: string }

/** Adds a typed code to the entered list (normalized, deduplicated, capped). */
export function addCouponCode(codes: readonly string[], raw: string): AddCouponCodeResult {
  const code = normalizeCouponCode(raw)
  if (!code) return { ok: false, error: "Digite um código de cupom." }
  if (codes.some((entered) => couponCodeKey(entered) === couponCodeKey(code))) return { ok: false, error: `O cupom ${code} já está no pedido.` }
  if (codes.length >= MAX_ENTERED_COUPON_CODES) {
    return { ok: false, error: "Você já adicionou o máximo de cupons para este pedido." }
  }
  return { ok: true, code, codes: [...codes, code] }
}

/** The entered list without `code`. */
export function removeCouponCode(codes: readonly string[], code: string): string[] {
  return codes.filter((entered) => entered !== code)
}

/**
 * The state of one entered code, as the coupon chips render it. `checking`
 * means there is no verdict for this exact bag yet, never a guess.
 */
export type CouponChip =
  | { code: string; status: "checking" }
  | { code: string; status: "applied"; discountCents: number }
  | { code: string; status: "blocked"; title: string; blockers: CouponBlocker[] }
  | { code: string; status: "dropped"; note: string }

export type CouponListView = {
  chips: CouponChip[]
  invalid: { code: string; message: string }[]
}

function allocatedCents(evaluation: CouponEvaluation, code: string): number {
  return evaluation.allocations
    .filter((allocation) => couponCodeKey(allocation.couponCode) === couponCodeKey(code))
    .reduce((sum, allocation) => sum + allocation.amountCents, 0)
}

/**
 * Pairs every entered code with the server's verdict for the current bag.
 * Codes the server will never accept for this order come back in `invalid`,
 * for the caller to take off the list and explain once. An applied coupon is
 * worth what was allocated for it — the very amounts the order will carry.
 */
export function couponListView(
  codes: readonly string[],
  evaluation: CouponEvaluation | null,
  cart: Cart,
): CouponListView {
  if (!evaluation) return { chips: codes.map((code) => ({ code, status: "checking" })), invalid: [] }

  const applied = new Set(evaluation.applied.map((coupon) => couponCodeKey(coupon.code)))
  const rejected = new Map(evaluation.rejected.map((coupon) => [couponCodeKey(coupon.code), coupon]))
  const view: CouponListView = { chips: [], invalid: [] }

  for (const code of codes) {
    if (applied.has(couponCodeKey(code))) {
      view.chips.push({ code, status: "applied", discountCents: allocatedCents(evaluation, code) })
      continue
    }
    const rejection = rejected.get(couponCodeKey(code))
    if (!rejection) {
      view.chips.push({ code, status: "checking" })
      continue
    }
    const notice = describeRejection(rejection, cart, evaluation.maxCouponsPerOrder)
    if (notice.kind === "invalid") view.invalid.push({ code, message: notice.message })
    else if (notice.kind === "dropped") view.chips.push({ code, status: "dropped", note: notice.note })
    else view.chips.push({ code, status: "blocked", title: notice.title, blockers: notice.blockers })
  }
  return view
}

/** The codes this verdict will never accept for the order, with the line that explains each. */
export function invalidCouponCodes(evaluation: CouponEvaluation): { code: string; message: string }[] {
  return evaluation.rejected.flatMap((rejected) => {
    const notice = describeRejection(rejected, { items: [], totalPrice: 0 }, evaluation.maxCouponsPerOrder)
    return notice.kind === "invalid" ? [{ code: notice.code, message: notice.message }] : []
  })
}

/** Codes this verdict refuses for good — every invalid one except a code that is only outside its weekly window. */
export function refusedForGoodCodes(evaluation: CouponEvaluation): string[] {
  return evaluation.rejected
    .filter((rejected) => rejected.reason !== "EVALUATION_REASON_OUTSIDE_AVAILABILITY" && isInvalidReason(rejected.reason))
    .map((rejected) => rejected.code)
}
